import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { supportedLocales, getPathWithoutLocale, getLocalizedPath } from '../i18n'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: string
  noindex?: boolean
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = "avatar cropper online, circle crop image tool, make image square, profile picture editor, free online tool, crop avatar, resize image, social media avatar, gaming profile picture, professional headshot, image cropper, photo editor, avatar maker, image cropping tool, crop profile picture, square image editor, circular image crop, avatar generator, profile photo cropper",
  image = "https://avatarcropper.org/og-image.jpg",
  url,
  type = "website",
  noindex = false
}) => {
  const intl = useIntl()
  const { locale } = useLanguage()
  const location = useLocation()
  
  // Use provided title/description or fall back to translated defaults
  const defaultTitle = intl.formatMessage({ id: 'meta.title' })
  const defaultDescription = intl.formatMessage({ id: 'meta.description' })
  
  const finalTitle = title || defaultTitle
  const finalDescription = description || defaultDescription
  const siteTitle = "AvatarCropper.org"
  const fullTitle = finalTitle.includes(siteTitle) ? finalTitle : `${finalTitle} | ${siteTitle}`

  // Generate canonical URL based on current path and locale
  const basePath = getPathWithoutLocale(location.pathname)
  const canonicalPath = getLocalizedPath(basePath, locale)
  const canonicalUrl = url || `https://avatarcropper.org${canonicalPath}`

  // Generate hreflang URLs for all supported languages
  const generateHreflangUrls = () => {
    return supportedLocales.map(lang => {
      const localizedPath = getLocalizedPath(basePath, lang)
      return {
        lang,
        url: `https://avatarcropper.org${localizedPath}`
      }
    })
  }

  const hreflangUrls = generateHreflangUrls()

  // Map locale codes to proper hreflang codes
  const getHreflangCode = (locale: string) => {
    const hreflangMap: Record<string, string> = {
      'en': 'en',
      'de': 'de',
      'es': 'es',
      'fr': 'fr',
      'ja': 'ja',
      'pt': 'pt',
      'ru': 'ru',
      'zh-cn': 'zh-CN',
      'zh-tw': 'zh-TW',
      'ko': 'ko',
      'vi': 'vi'
    }
    return hreflangMap[locale] || locale
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={keywords} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={
        locale === 'de' ? 'de_DE' :
        locale === 'ja' ? 'ja_JP' : 
        locale === 'es' ? 'es_ES' :
        locale === 'fr' ? 'fr_FR' :
        locale === 'pt' ? 'pt_PT' :
        locale === 'ru' ? 'ru_RU' :
        locale === 'zh-cn' ? 'zh_CN' :
        locale === 'zh-tw' ? 'zh_TW' :
        locale === 'ko' ? 'ko_KR' :
        locale === 'vi' ? 'vi_VN' :
        'en_US'
      } />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={finalDescription} />
      <meta property="twitter:image" content={image} />
      
      {/* Additional SEO Tags */}
      <meta name="author" content="AvatarCropper.org" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content={locale} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language alternatives - hreflang tags */}
      {hreflangUrls.map(({ lang, url: hrefUrl }) => (
        <link 
          key={lang} 
          rel="alternate" 
          hrefLang={getHreflangCode(lang)} 
          href={hrefUrl} 
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`https://avatarcropper.org${basePath || '/'}`} />
      
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Avatar Cropper",
          "description": finalDescription,
          "url": canonicalUrl,
          "applicationCategory": "PhotographyApplication",
          "operatingSystem": "Web Browser",
          "inLanguage": locale,
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "AvatarCropper.org"
          }
        })}
      </script>
    </Helmet>
  )
}

export default SEO
